/**
 * SoleadLab 2.0 - Thermal Advanced Parameters
 *
 * Collapsible section with 5 radiometric parameter inputs.
 * Changing values triggers temperature recalculation.
 */

import { useState } from 'react';
import type { RadiometricParams } from './thermalTypes';
import './ThermalAdvancedParams.css';

interface ThermalAdvancedParamsProps {
  params: RadiometricParams;
  onChange: (params: RadiometricParams) => void;
  disabled?: boolean;
}

export default function ThermalAdvancedParams({
  params,
  onChange,
  disabled = false,
}: ThermalAdvancedParamsProps) {
  const [expanded, setExpanded] = useState(false);

  const handleField = (field: keyof RadiometricParams, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    onChange({ ...params, [field]: val });
  };

  return (
    <div className="thermal-params">
      <button
        type="button"
        className="thermal-params__toggle"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={`thermal-params__chevron${expanded ? ' thermal-params__chevron--open' : ''}`}>
          &#9654;
        </span>
        Advanced Parameters
      </button>

      {expanded && (
        <div className="thermal-params__grid">
          <label className="thermal-params__label">
            Emissivity
            <input
              type="number"
              className="thermal-params__input"
              value={params.emissivity}
              min={0.01}
              max={1}
              step={0.01}
              disabled={disabled}
              onChange={(e) => handleField('emissivity', e.target.value)}
            />
          </label>
          <label className="thermal-params__label">
            Distance (m)
            <input
              type="number"
              className="thermal-params__input"
              value={params.distance_m}
              min={1}
              max={1000}
              step={1}
              disabled={disabled}
              onChange={(e) => handleField('distance_m', e.target.value)}
            />
          </label>
          <label className="thermal-params__label">
            Humidity (%)
            <input
              type="number"
              className="thermal-params__input"
              value={params.humidity_pct}
              min={0}
              max={100}
              step={1}
              disabled={disabled}
              onChange={(e) => handleField('humidity_pct', e.target.value)}
            />
          </label>
          <label className="thermal-params__label">
            Air Temp (°C)
            <input
              type="number"
              className="thermal-params__input"
              value={params.air_temp_c}
              min={-40}
              max={80}
              step={0.5}
              disabled={disabled}
              onChange={(e) => handleField('air_temp_c', e.target.value)}
            />
          </label>
          <label className="thermal-params__label">
            Reflected Temp (°C)
            <input
              type="number"
              className="thermal-params__input"
              value={params.reflected_temp_c}
              min={-40}
              max={80}
              step={0.5}
              disabled={disabled}
              onChange={(e) => handleField('reflected_temp_c', e.target.value)}
            />
          </label>
        </div>
      )}
    </div>
  );
}
